import {envIsDev} from '../../../../config';
import {onRumViewStart, recordRumDurationVital} from '../../datadogRum';
import {PERF_WINDOW_SAMPLING_VITAL_NAME} from './constants';
import {installInteractionTrigger} from './interactionTrigger';
import {LifecyclePerfObserver} from './lifecyclePerfObserver';
import {
  LoafObservationRuntime,
  type ReportProfilerUnavailable,
} from './loafObservationRuntime';
import {createSamplingPolicy} from './samplingPolicy';
import type {
  PerfWindowObservationOptions,
  PerfWindowSessionHandle,
  PerfWindowSessionOptions,
  RecordPerfWindowVital,
} from './types';
import {WindowedPerfObserver} from './windowedPerfObserver';

export {
  PERF_WINDOW_SAMPLING_VITAL_NAME,
  PERF_WINDOW_VITAL_NAME,
} from './constants';

const shouldLogToConsole = envIsDev && false;

const defaultRecordVital: RecordPerfWindowVital = (name, options) => {
  if (shouldLogToConsole) {
    console.info(name, options);
  }
  recordRumDurationVital(name, options);
};

export class PerfWindowMonitor {
  private readonly runtime: LoafObservationRuntime;
  private readonly windowedObserver: WindowedPerfObserver;
  private readonly lifecycleObserver: LifecyclePerfObserver;
  private initializationCount = 0;
  private stopSharedListeners: (() => void) | undefined;

  constructor(
    private readonly recordVital: RecordPerfWindowVital = defaultRecordVital,
    reportProfilerUnavailable?: ReportProfilerUnavailable
  ) {
    // Windows and sessions of the same trigger and workflow share a cooldown.
    // Switching between them must not allow an extra start.
    const samplingPolicy = createSamplingPolicy();
    this.runtime = new LoafObservationRuntime(
      reportProfilerUnavailable ?? this.reportUnsupported
    );
    this.windowedObserver = new WindowedPerfObserver(
      this.runtime,
      recordVital,
      samplingPolicy
    );
    this.lifecycleObserver = new LifecyclePerfObserver(
      this.runtime,
      recordVital,
      samplingPolicy
    );
  }

  initialize(): () => void {
    const stopEventTiming = this.windowedObserver.initialize();
    if (!this.runtime.isSupported()) {
      return stopEventTiming;
    }

    // Multiple consumers can mount the monitor. Share DOM listeners so a single
    // input is not counted repeatedly, and retain them until the last cleanup.
    this.initializationCount += 1;
    if (this.initializationCount === 1) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('freeze', this.handleFreeze);
      const stopViewStart = onRumViewStart(this.handleViewStart);
      const stopInteractions = installInteractionTrigger(this.observe);
      this.stopSharedListeners = () => {
        document.removeEventListener(
          'visibilitychange',
          this.handleVisibilityChange
        );
        window.removeEventListener('pagehide', this.handlePageHide);
        document.removeEventListener('freeze', this.handleFreeze);
        stopViewStart();
        stopInteractions();
      };
    }

    let stopped = false;
    return () => {
      if (stopped) {
        return;
      }
      stopped = true;
      stopEventTiming();
      this.initializationCount -= 1;
      if (this.initializationCount === 0) {
        this.stopSharedListeners?.();
        this.stopSharedListeners = undefined;
        this.windowedObserver.dispose();
        this.lifecycleObserver.dispose();
      }
    };
  }

  readonly observe = (options: PerfWindowObservationOptions): boolean =>
    this.windowedObserver.observe(options);

  beginSession(
    options: PerfWindowSessionOptions
  ): PerfWindowSessionHandle | undefined {
    return this.lifecycleObserver.begin(options);
  }

  private readonly reportUnsupported: ReportProfilerUnavailable =
    missingCapabilities => {
      this.recordVital(PERF_WINDOW_SAMPLING_VITAL_NAME, {
        startTime: Date.now(),
        duration: 0,
        context: {
          outcome: 'unsupported',
          missingCapabilities,
        },
      });
    };

  private readonly handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.windowedObserver.markBackgrounded();
      this.lifecycleObserver.markBackgrounded();
    }
  };

  // Flush under the old RUM view so an unfinished sample cannot cross route attribution.
  private readonly handleViewStart = () => {
    this.windowedObserver.interruptForViewChange();
  };

  private readonly handlePageHide = () => {
    this.handlePageAbandonment('pagehide');
  };

  private readonly handleFreeze = () => {
    this.handlePageAbandonment('freeze');
  };

  private handlePageAbandonment(reason: 'pagehide' | 'freeze'): void {
    this.lifecycleObserver.handlePageAbandonment(reason);
    this.windowedObserver.handlePageAbandonment(reason);
  }
}

const perfWindowMonitor = new PerfWindowMonitor();

export const initializePerfWindowMonitoring = (): (() => void) =>
  perfWindowMonitor.initialize();

export const observePerfWindow = (
  options: PerfWindowObservationOptions
): boolean => perfWindowMonitor.observe(options);

export const beginPerfWindowSession = (
  options: PerfWindowSessionOptions
): PerfWindowSessionHandle | undefined =>
  perfWindowMonitor.beginSession(options);
